import * as React from 'react'
import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'

import * as base from './api'

export type UserOrderParams = {
  page?: number
  limit?: number
  filter?: {
    projectId?: number | string
    status?: api.OrderStatus
  }
  recipient_user_id?: number
}

export function toURLSearchParams({ limit, filter, page, recipient_user_id }: UserOrderParams): URLSearchParams {
  const searchParams = new URLSearchParams()

  if (page !== undefined) {
    searchParams.set('offset', String((page - 1) * (limit ?? 20)))
  }
  if (limit !== undefined) {
    searchParams.set('limit', String(limit))
  }
  if (filter?.projectId) {
    searchParams.set('projectId', String(filter.projectId))
  }
  if (filter?.status) {
    searchParams.set('status', String(filter.status))
  }
  if (recipient_user_id !== undefined) {
    searchParams.set('recipient_user_id', String(recipient_user_id))
  }
  return searchParams
}

type OrderPage = api.Pageable<api.OrderList<true>>

const USER_ORDERS_CACHE = jotai.atom<hooks.Cache<OrderPage>>({})

export type UserOrdersConfig = {
  fetchOnMount?: boolean | 'force'
  params?: UserOrderParams
}

export type UseUserOrders = {
  response: hooks.Response<OrderPage>
  query: () => Promise<void>
  markAsDelivered: (orderId: number) => Promise<void>
}

export function useUserOrders(
  userId?: number,
  { fetchOnMount = true, params = {} }: UserOrdersConfig = {},
): UseUserOrders {
  const sharedQuery = hooks.useSharedQuery(
    (userId: number, params: UserOrderParams) => {
      return base.listForUser(userId, toURLSearchParams(params))
    },
    {
      atom: USER_ORDERS_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(userId, params))

  return { response: getResponse(), query, markAsDelivered: markOrderAsDelivered }

  function getResponse(): hooks.Response<OrderPage> {
    if (userId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }
    return sharedQuery.getCurrentResponse([userId, params])
  }

  async function query(): Promise<void> {
    if (userId !== undefined) {
      return sharedQuery.query(userId, params)
    }
  }

  async function markOrderAsDelivered(orderId: number): Promise<void> {
    if (userId === undefined) {
      return
    }

    sharedQuery.patch([userId, params], async (response) => {
      const result = await base.markAsDelivered(orderId)
      const data = response.data

      // nothing in cache to mutate to mutate
      if (data === undefined) {
        return undefined
      }

      return result.next((updatedOrder) => {
        const orders = [...data.orders]
        const index = orders.findIndex((order) => order.id === updatedOrder.id)
        if (index >= 0) {
          orders[index] = updatedOrder
        }

        return { ...data, orders }
      })
    })
  }

  function id(userId: number | undefined, config: UserOrderParams): string {
    return JSON.stringify([
      userId,
      config.page ?? 0,
      config.limit ?? 10,
      config.filter?.projectId,
      config.filter?.status,
      config.recipient_user_id,
    ])
  }
}
